import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { Modal, AcknowledgeModal, Button, Text } from '@percent/lemonade'
import { useMutation, useLogger } from '@percent/workplace-giving/common/hooks'
import { publishCampaign } from '@percent/workplace-giving/api/campaigns/publishCampaign/publishCampaign'
import { okResponse } from '@percent/workplace-giving/api/goodstack'
import { Banner } from '@percent/workplace-giving/common/components/Banner/Banner'

interface CampaignDraftBannerProps {
  campaignId: string
  onPublishSuccess?: () => void
}

export function CampaignDraftBanner({ campaignId, onPublishSuccess }: CampaignDraftBannerProps) {
  const { t } = useTranslation()
  const { logError } = useLogger()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)

  const { mutateAsync: publishCampaignMutation, isLoading } = useMutation({
    mutationFn: publishCampaign,
    onSuccess: response => {
      if (okResponse(response)) {
        setShowSuccessModal(true)
      } else {
        logError(response.error)
        setShowErrorModal(true)
      }
    },
    onError: error => {
      logError(error)
      setShowErrorModal(true)
    }
  })

  const handlePublishCampaign = () => publishCampaignMutation([campaignId])

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    onPublishSuccess?.()
  }

  const handleCloseErrorModal = () => {
    setShowErrorModal(false)
  }

  return (
    <>
      <Banner testId="campaign-draft-banner">
        <Text fontWeight="medium" size="small">
          {t('workplace_giving.campaigns.banner.draft')}
        </Text>
        <Text size="small">{t('workplace_giving.campaigns.banner.draftCTA')}</Text>
        <Button
          variant="primary"
          shape="oval"
          size="small"
          onClick={handlePublishCampaign}
          disabled={isLoading}
          loading={isLoading}
          data-testid="publish-campaign-button"
          data-loading={isLoading}
        >
          {t('workplace_giving.common.publish')}
        </Button>
      </Banner>

      <Modal open={showSuccessModal} borderRadiiSize="xlarge">
        <AcknowledgeModal
          result="positive"
          title={t('workplace_giving.campaigns.campaignForm.create.publicationSuccessModal.title')}
          description={t('workplace_giving.campaigns.publish.confirmationDetails')}
          buttonText={t('workplace_giving.common.done')}
          handleClose={handleCloseSuccessModal}
          buttonShape="oval"
        />
      </Modal>

      <Modal open={showErrorModal} borderRadiiSize="xlarge">
        <AcknowledgeModal
          result="negative"
          title={t('workplace_giving.errors.somethingWentWrong')}
          description={t('workplace_giving.errors.tryAgainLater')}
          buttonText={t('workplace_giving.common.done')}
          handleClose={handleCloseErrorModal}
          buttonShape="oval"
        />
      </Modal>
    </>
  )
}
